import React from 'react';
import cx from 'classnames';

import {
    IconDownload,
    LoadingSpinner,
    Tooltip,
    Button,
} from '@trqk/components';
import { locale } from '@trqk/utils';

import { DownloadAs } from '../../../tooltips';
import { useGapAnalysisTools } from '../../../../hooks';

interface DownloadAsButtonProps {
    beforeDownload?: () => Promise<boolean>;
    gapAnalysisId: string | null;
    savingChannelSelection: boolean;
}

/**
 * A button that opens the DownloadAs tooltip.
 * @param props - {@link DownloadAsButtonProps}
 * @returns JSX
 */
export const DownloadAsButton = ({
    beforeDownload,
    gapAnalysisId,
    savingChannelSelection,
}: DownloadAsButtonProps): JSX.Element => {
    const { exportGapAnalysis, exportingGapAnalysisIdsWithFilename } =
        useGapAnalysisTools();

    const exporting =
        gapAnalysisId !== null &&
        exportingGapAnalysisIdsWithFilename.includes(gapAnalysisId);
    const loading = savingChannelSelection || exporting;

    return (
        <Tooltip<DownloadAsTooltipMessageProps>
            addClasses={cx('top-1.5 relative', {
                'cursor-default text-tertiary-300': loading,
            })}
            disabled={loading}
            trigger="click"
            RenderMessage={DownloadAs}
            renderMessageProps={{
                handleDownloadAs: (downloadAs: string) => {
                    if (gapAnalysisId) {
                        if (beforeDownload)
                            beforeDownload().then(success => {
                                if (success) {
                                    exportGapAnalysis(
                                        gapAnalysisId,
                                        downloadAs,
                                    );
                                }
                            });
                        else {
                            exportGapAnalysis(gapAnalysisId, downloadAs);
                        }
                    }
                },
                fileExtension: '.zip',
            }}
            minWidth={450}
            maxWidth={450}
            position="left"
            background="light"
        >
            <Button
                addClasses="respond-to-mouse"
                autoWidth={true}
                color="primary-light-bg"
                disabled={loading}
            >
                <p className="flex items-center px-4 leading-6">
                    {!loading && <IconDownload width={24} fillColor="white" />}
                    {loading && <LoadingSpinner width={24} />}
                    <span className="ml-2">
                        {
                            locale.enUS.GAP_ANALYSIS.REPORT_VIEWER_TABLE.BUTTONS
                                .DOWNLOAD_AS
                        }
                    </span>
                </p>
            </Button>
        </Tooltip>
    );
};
